// #1920 — bug #183's screenshot ("Failed to fetch", thrown out of the
// minified module-preload helper's async import chain) is a missing-chunk
// error: a stale precached index.html still references a bundle hash a
// later deploy has already removed from the CDN. Vite's own runtime dispatches
// a `vite:preloadError` window event whenever any dynamically-imported
// module fails to load — this listens for it and self-heals once.
//
// #2170 — there is no React error boundary anywhere in this app (grep
// ErrorBoundary/componentDidCatch/getDerivedStateFromError across
// apps/scribe-fe/src and packages/common-fe/src: zero hits; #1919 shipped
// AuthGate's fatal branch in CognitoAuthProvider.jsx, gated on an OIDC
// auth error, which a vite:preloadError never sets). If this handler does
// not call event.preventDefault(), Vite's own preload helper rethrows the
// original error (node_modules/vite/dist/node/chunks/dep-*.js,
// handlePreloadError: `if (!e.defaultPrevented) throw err;`), which
// propagates as an uncaught rejection with nothing downstream to catch it.
// So event.preventDefault() is called unconditionally below, on every
// route, safe or not: leaving a preloadError unhandled is strictly worse
// than reloading, and this card's route gate is about WHEN to reload, not
// whether to let the app crash instead.
//
// Same side effect as ReloadPrompt.jsx's SW-update auto-apply path (a full
// page reload) via a different trigger (a failed dynamic import, not an
// available update), so it consults the same shouldAutoApplyUpdate route
// gate (#1920) before reloading, and mirrors ReloadPrompt's own shape for
// the unsafe case: DEFER, don't drop. On a route pwaUpdatePolicy.js's
// SAFE_ROUTES does not certify (the scoring sheet, the class grid, /callback,
// and — since it is now an allow-list — any future route nobody has added
// to the list yet), forceUpdateAndReload is not called yet; a pending flag
// is set instead, and installChunkLoadRecovery's caller (ReloadPrompt.jsx)
// retries it via retryPendingChunkRecovery on every route change, the exact
// mechanism #1920 already built for re-evaluating the SW-update case the
// instant a scribe reaches a safe route. Whatever dynamic-import call site
// failed stays failed for this page load (nothing here can retroactively
// resolve an already-rejected import()), but nothing here forces a reload
// while unsaved obsDrafts/totalDraft/placeDraft are sitting in React state,
// and the stale shell still gets healed via reload the moment the scribe
// leaves the unsafe route, rather than staying wedged for the rest of the
// session (the DROP alternative's failure mode).
//
// One recovery attempt per session either way: once forceUpdateAndReload
// actually runs (immediately on a safe route, or later via the deferred
// retry), the sessionStorage guard lets a further failure fall through to
// whatever actually happens when nothing catches an unhandled preloadError.
import { shouldAutoApplyUpdate } from "./pwaUpdatePolicy.js";

const RECOVERY_ATTEMPTED_KEY = "scribe-fe.chunkLoadRecoveryAttempted";

// Module-scoped: set when a preloadError arrives on a route the safety gate
// has not certified. Survives across route changes for the life of this
// page load (no reload has happened yet); consumed once by
// retryPendingChunkRecovery the first time it is called from a safe route.
let pendingRecovery = false;

function hasAttemptedRecovery() {
  try {
    return sessionStorage.getItem(RECOVERY_ATTEMPTED_KEY) === "1";
  } catch {
    // sessionStorage unavailable (private browsing) — treat as "not yet
    // attempted" so we still try once; worst case is one extra reload.
    return false;
  }
}

function markRecoveryAttempted() {
  try {
    sessionStorage.setItem(RECOVERY_ATTEMPTED_KEY, "1");
  } catch {
    // non-fatal — see hasAttemptedRecovery
  }
}

// Forces the waiting worker (if any) to activate, then reloads. Exported
// for the Cypress spec to await directly instead of racing a real reload.
export async function forceUpdateAndReload() {
  markRecoveryAttempted();
  if (navigator.serviceWorker) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update();
        if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
        }
      }
    } catch {
      // Registration/update lookup failed (offline, no SW support) — fall
      // through to the reload anyway, it is the best remaining recovery.
    }
  }
  window.location.reload();
}

export function installChunkLoadRecovery() {
  window.addEventListener("vite:preloadError", (event) => {
    if (hasAttemptedRecovery()) return; // let the second failure surface normally
    // See the file-header comment: preventDefault unconditionally, an
    // unhandled preloadError has nothing downstream to catch it.
    event.preventDefault();
    if (shouldAutoApplyUpdate(window.location.pathname)) {
      forceUpdateAndReload();
    } else {
      pendingRecovery = true;
    }
  });
}

// Called by ReloadPrompt.jsx on every route change (the same re-evaluation
// point #1920 built for the SW-update case). Replays a deferred recovery
// the first time the route becomes safe; a no-op otherwise.
export function retryPendingChunkRecovery(pathname) {
  if (!pendingRecovery) return;
  if (hasAttemptedRecovery()) {
    pendingRecovery = false; // recovered some other way already; stop asking
    return;
  }
  if (!shouldAutoApplyUpdate(pathname)) return; // still unsafe, keep deferring
  pendingRecovery = false;
  forceUpdateAndReload();
}
